/* ============================================================
   DIPANNITA SINGH — Interactive Enhancement Layer
   1. Scroll reveal       — sections + product cards fade/rise in
   2. Custom cursor       — rose gold dot with hover expand
   3. Hero parallax       — background drifts at 30% scroll speed
   4. Marquee velocity    — strip accelerates with scroll speed
   ============================================================ */

(function () {
  'use strict';

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch = () => window.matchMedia('(hover: none)').matches;

  /* ─────────────────────────────────────────────────────────
     1. SCROLL REVEAL
     Elements start invisible (opacity 0, translateY 28px)
     and transition in when they enter the viewport.
     Product cards stagger based on their position in the grid.
  ───────────────────────────────────────────────────────── */
  function initScrollReveal() {
    if (prefersReduced) return;

    const selectors = [
      '.ds-about__eyebrow',
      '.ds-about__title',
      '.ds-about__intro-grid',
      '.ds-about__pillar',
      '.ds-lookbook__item',
      '.ds-marquee',
      'product-card',
    ].join(',');

    const elements = document.querySelectorAll(selectors);

    elements.forEach((el) => {
      // Stagger product cards based on their sibling index
      if (el.matches('product-card')) {
        const parent = el.parentElement;
        if (parent) {
          const siblings = Array.from(parent.querySelectorAll(':scope > product-card, :scope > li > product-card'));
          const idx = siblings.indexOf(el);
          el.style.transitionDelay = Math.min(idx * 0.08, 0.48) + 's';
        }
      }
      el.classList.add('ds-reveal');
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('ds-visible');
            // Keep observing product cards — they can re-render on variant change
            if (!entry.target.matches('product-card')) {
              observer.unobserve(entry.target);
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    elements.forEach((el) => observer.observe(el));
  }

  /* ─────────────────────────────────────────────────────────
     2. CUSTOM CURSOR
     A rose gold dot that smoothly follows the mouse (lerp 12%).
     Expands to a ring when hovering interactive elements.
     Only active on non-touch, non-reduced-motion devices.
  ───────────────────────────────────────────────────────── */
  function initCursor() {
    if (prefersReduced || isTouch()) return;

    const dot = document.createElement('div');
    dot.className = 'ds-cursor';
    dot.setAttribute('aria-hidden', 'true');
    document.body.appendChild(dot);

    dot.style.transform = 'translate(-200px, -200px)';

    document.addEventListener('mousemove', (e) => {
      dot.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    });

    document.addEventListener('mouseleave', () => {
      dot.style.opacity = '0';
    });

    document.addEventListener('mouseenter', () => {
      dot.style.opacity = '1';
    });

    const interactable = 'a, button, [role="button"], input, label, select, textarea, .ds-lookbook__item, product-card, .ds-btn';

    document.addEventListener('mouseover', (e) => {
      if (e.target.closest(interactable)) dot.classList.add('ds-cursor--hover');
    });

    document.addEventListener('mouseout', (e) => {
      if (e.target.closest(interactable)) dot.classList.remove('ds-cursor--hover');
    });

    // Native <dialog> modals (cart drawer, search, quick-add) render in
    // the browser's top layer, which sits above fixed-position elements
    // regardless of z-index. Promoting the dot into the top layer via the
    // Popover API (rather than reparenting it into the dialog) keeps it
    // visible above any open dialog without disturbing its DOM position —
    // dialogs like the cart drawer animate with a CSS transform, which
    // would otherwise change the fixed-position containing block if the
    // dot were moved inside them.
    if ('showPopover' in dot) {
      dot.setAttribute('popover', 'manual');

      const promoteCursor = () => {
        try {
          if (dot.matches(':popover-open')) dot.hidePopover();
          dot.showPopover();
        } catch (err) {
          /* no-op: popover already in the desired state */
        }
      };

      promoteCursor();

      const dialogObserver = new MutationObserver(promoteCursor);
      dialogObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['open'],
        subtree: true,
      });
    }
  }

  /* ─────────────────────────────────────────────────────────
     3. HERO PARALLAX
     The hero background image scrolls at 30% of page scroll
     speed, creating a depth / cinematic effect.
     Listener is only active while the hero section is visible.
  ───────────────────────────────────────────────────────── */
  function initHeroParallax() {
    if (prefersReduced) return;

    const heroBg = document.querySelector('.ds-hero__bg-img');
    if (!heroBg) return;

    const heroSection = heroBg.closest('.ds-hero');
    if (!heroSection) return;

    let ticking = false;

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(() => {
          heroBg.style.transform = `scale(1.1) translateY(${window.scrollY * 0.28}px)`;
          ticking = false;
        });
        ticking = true;
      }
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          window.addEventListener('scroll', onScroll, { passive: true });
        } else {
          window.removeEventListener('scroll', onScroll);
        }
      },
      { threshold: 0 }
    );

    // Pre-scale so parallax doesn't reveal edges
    heroBg.style.transform = 'scale(1.1)';
    observer.observe(heroSection);
  }

  /* ─────────────────────────────────────────────────────────
     4. MARQUEE VELOCITY
     Fast scrolling makes the strip run faster, then it eases
     back to the base speed (30s) as the page settles.
  ───────────────────────────────────────────────────────── */
  function initMarqueeVelocity() {
    if (prefersReduced) return;

    const track = document.querySelector('.ds-marquee__track');
    if (!track) return;

    const BASE = 30;   // seconds — resting speed
    const FAST = 10;   // seconds — max speed under fast scroll

    let lastScrollY = window.scrollY;
    let lastTime = performance.now();
    let targetDuration = BASE;
    let currentDuration = BASE;
    let ticking = false;

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            const now = performance.now();
            const dt = Math.max(now - lastTime, 1);
            const velocity = Math.abs(window.scrollY - lastScrollY) / dt; // px/ms

            // More velocity → shorter (faster) duration
            targetDuration = BASE - Math.min(velocity * 200, BASE - FAST);

            lastScrollY = window.scrollY;
            lastTime = now;
            ticking = false;
          });
          ticking = true;
        }
      },
      { passive: true }
    );

    (function tick() {
      // Lerp current toward target; decay target back to base
      currentDuration += (targetDuration - currentDuration) * 0.06;
      targetDuration += (BASE - targetDuration) * 0.03;
      track.style.animationDuration = currentDuration.toFixed(2) + 's';
      requestAnimationFrame(tick);
    })();
  }

  /* ─────────────────────────────────────────────────────────
     INIT — wait for DOM if needed
  ───────────────────────────────────────────────────────── */
  function init() {
    initScrollReveal();
    initCursor();
    initHeroParallax();
    initMarqueeVelocity();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
